import React, { useState, useEffect } from "react";
import { FiMapPin, FiPlus, FiEdit2, FiTrash2, FiX, FiUsers, FiGrid, FiMap, FiBarChart2 } from "react-icons/fi";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import api from "../../api";
import "./States.css";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// State name to abbreviation mapping
const stateNameToAbbr = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
  "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA",
  "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA",
  "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
  "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS", "Missouri": "MO",
  "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH",
  "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT",
  "Virginia": "VA", "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY",
  "District of Columbia": "DC", "Puerto Rico": "PR"
};

const States = ({ showToast, currentUser }) => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState(null);
  const [counties, setCounties] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignFormData, setAssignFormData] = useState({
    divisionId: "",
    userId: "",
    notes: ""
  });
  const [viewMode, setViewMode] = useState("grid"); // "grid", "map", or "analytics"
  const [hoveredState, setHoveredState] = useState(null);
  const [analytics, setAnalytics] = useState({ divisions: [], users: [] });
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [stateRegions, setStateRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [stateMetrics, setStateMetrics] = useState([]);
  const [metricFilter, setMetricFilter] = useState('division'); // 'division', 'people', 'companies', 'sales', 'boxes'
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  useEffect(() => {
    loadStates();
    loadDivisions();
    loadTeamMembers();
    loadStateMetrics();
  }, []);

  useEffect(() => {
    if (viewMode === "analytics") {
      loadAnalytics();
    }
  }, [viewMode]);

  const loadStates = async () => {
    try {
      setLoading(true);
      console.log("[States] Loading states from API...");
      const response = await api.get("/territories/states");
      console.log("[States] States loaded successfully:", response.data);
      setStates(response.data);
    } catch (error) {
      console.error("[States] Error loading states:", error);
      console.error("[States] Error response:", error.response);
      console.error("[States] Error message:", error.message);
      showToast && showToast(`Failed to load states: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadDivisions = async () => {
    try {
      const response = await api.get("/territories/divisions");
      setDivisions(response.data);
    } catch (error) {
      console.error("Error loading divisions:", error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await api.get("/territories/team-members");
      setTeamMembers(response.data);
    } catch (error) {
      console.error("Error loading team members:", error);
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoadingAnalytics(true);
      const response = await api.get("/territories/analytics");
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error loading analytics:", error);
      showToast && showToast("Failed to load analytics", "error");
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadStateMetrics = async () => {
    try {
      setLoadingMetrics(true);
      const response = await api.get("/territories/states/metrics");
      setStateMetrics(response.data);
    } catch (error) {
      console.error("Error loading state metrics:", error);
      showToast && showToast("Failed to load state metrics", "error");
    } finally {
      setLoadingMetrics(false);
    }
  };

  const loadCounties = async (stateId, regionId = null) => {
    try {
      const url = regionId 
        ? `/territories/states/${stateId}/counties?regionId=${regionId}`
        : `/territories/states/${stateId}/counties`;
      const response = await api.get(url);
      setCounties(response.data);
    } catch (error) {
      console.error("Error loading counties:", error);
      showToast && showToast("Failed to load counties", "error");
    }
  };

  const handleStateClick = (state) => {
    setSelectedState(state);
    loadCounties(state.id, state.regionId);
  };

  const openAssignModal = async (state) => {
    setSelectedState(state);
    setAssignFormData({
      assignmentId: state.assignmentId || "",
      divisionId: state.divisionId || "",
      userId: state.userId || "",
      regionId: state.regionId || "",
      notes: ""
    });
    
    // Load regions for this state
    try {
      setLoadingRegions(true);
      const response = await api.get(`/territories/states/${state.id}/regions`);
      setStateRegions(response.data);
    } catch (error) {
      console.error("Error loading regions:", error);
      setStateRegions([]);
    } finally {
      setLoadingRegions(false);
    }
    
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setAssignFormData({ assignmentId: "", divisionId: "", userId: "", regionId: "", notes: "" });
    setStateRegions([]);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/territories/states/${selectedState.id}/assign`, assignFormData);
      showToast && showToast("State assigned successfully", "success");
      loadStates();
      closeAssignModal();
    } catch (error) {
      console.error("Error assigning state:", error);
      showToast && showToast("Failed to assign state", "error");
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm("Remove this assignment?")) return;
    
    try {
      await api.delete(`/territories/assignments/${assignmentId}`);
      showToast && showToast("Assignment removed successfully", "success");
      loadStates();
      closeAssignModal();
    } catch (error) {
      console.error("Error removing assignment:", error);
      showToast && showToast("Failed to remove assignment", "error");
    }
  };

  if (loading) {
    return (
      <div className="states-container">
        <div className="loading-message">Loading states...</div>
      </div>
    );
  }

  if (states.length === 0) {
    return (
      <div className="states-container">
        <div className="empty-state">
          <FiMapPin size={48} />
          <p>No states found</p>
          <p className="empty-state-hint">
            Check the browser console (F12) for error details.
          </p>
          <button className="btn-primary" onClick={loadStates}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Helper function to get state data by name (from map)
  const getStateByName = (stateName) => {
    const abbr = stateNameToAbbr[stateName];
    if (!abbr) return null;
    return states.find(s => s.abbreviation === abbr);
  };

  // Helper function to get state color based on division and user assignment
  const getStateColor = (stateData) => {
    if (!stateData) return "#E5E7EB";

    if (metricFilter === 'division') {
      // Original division-based coloring
      if (!stateData.divisionColor) return "#E5E7EB";
      if (stateData.userName) return stateData.divisionColor;
      const hex = stateData.divisionColor;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, 0.4)`;
    }

    // Find metrics for this state/region
    const metrics = stateMetrics.find(m => 
      m.stateId === stateData.id && 
      ((m.regionId === stateData.regionId) || (!m.regionId && !stateData.regionId))
    );

    if (!metrics) return "#E5E7EB";

    const getValue = () => {
      switch(metricFilter) {
        case 'people': return metrics.peopleCount;
        case 'companies': return metrics.companyCount;
        case 'sales': return metrics.salesTotal;
        case 'boxes': return metrics.boxCount;
        default: return 0;
      }
    };

    const value = getValue();
    if (value === 0) return "#E5E7EB";

    // Get max value across all states for normalization
    const maxValue = Math.max(...stateMetrics.map(m => {
      switch(metricFilter) {
        case 'people': return m.peopleCount;
        case 'companies': return m.companyCount;
        case 'sales': return m.salesTotal;
        case 'boxes': return m.boxCount;
        default: return 0;
      }
    }));
    
    const intensity = maxValue > 0 ? Math.min(value / maxValue, 1) : 0;
    
    // Color scale: light blue to dark blue
    const r = Math.floor(59 + (228 - 59) * (1 - intensity));
    const g = Math.floor(130 + (240 - 130) * (1 - intensity));
    const b = 246;
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div className="states-container">
      <div className="states-header">
        <div>
          <h2>
            <FiMapPin /> States & Territories
          </h2>
          <p className="states-description">
            Manage state and county territories for team assignments and funding opportunities.
          </p>
        </div>
        <div className="view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewMode("grid")}
            title="Grid View"
          >
            <FiGrid /> Grid
          </button>
          <button
            className={`view-toggle-btn ${viewMode === "map" ? "active" : ""}`}
            onClick={() => setViewMode("map")}
            title="Map View"
          >
            <FiMap /> Map
          </button>
          <button
            className={`view-toggle-btn ${viewMode === "analytics" ? "active" : ""}`}
            onClick={() => setViewMode("analytics")}
            title="Analytics View"
          >
            <FiBarChart2 /> Analytics
          </button>
        </div>
      </div>

      {viewMode === "analytics" ? (
        <div className="analytics-view">
          {loadingAnalytics ? (
            <div className="loading-message">Loading analytics...</div>
          ) : (
            <>
              {/* Overall Metrics Summary */}
              <div className="analytics-section">
                <h3>Overall Metrics</h3>
                <div className="analytics-cards">
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <h4>People</h4>
                    </div>
                    <div className="analytics-card-body">
                      <div className="stat-value" style={{ fontSize: '32px', textAlign: 'center', padding: '20px' }}>
                        {stateMetrics.reduce((sum, m) => sum + m.peopleCount, 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <h4>Companies</h4>
                    </div>
                    <div className="analytics-card-body">
                      <div className="stat-value" style={{ fontSize: '32px', textAlign: 'center', padding: '20px' }}>
                        {stateMetrics.reduce((sum, m) => sum + m.companyCount, 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <h4>Total Sales</h4>
                    </div>
                    <div className="analytics-card-body">
                      <div className="stat-value" style={{ fontSize: '32px', textAlign: 'center', padding: '20px' }}>
                        ${stateMetrics.reduce((sum, m) => sum + m.salesTotal, 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <h4>Boxes Deployed</h4>
                    </div>
                    <div className="analytics-card-body">
                      <div className="stat-value" style={{ fontSize: '32px', textAlign: 'center', padding: '20px' }}>
                        {stateMetrics.reduce((sum, m) => sum + m.boxCount, 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Division Analytics */}
              <div className="analytics-section">
                <h3>Division Overview</h3>
                <div className="analytics-cards">
                  {analytics.divisions.map((div) => (
                    <div key={div.id} className="analytics-card">
                      <div className="analytics-card-header" style={{ borderLeftColor: div.color }}>
                        <h4>{div.name}</h4>
                        <div className="analytics-badge" style={{ backgroundColor: div.color }}>
                          {div.stateCount} states
                        </div>
                      </div>
                      <div className="analytics-card-body">
                        <div className="analytics-stat">
                          <span className="stat-label">Total Population</span>
                          <span className="stat-value">
                            {div.totalPopulation ? div.totalPopulation.toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        <div className="analytics-stat">
                          <span className="stat-label">Assigned to Reps</span>
                          <span className="stat-value">{div.assignedStates} states</span>
                        </div>
                        <div className="analytics-stat">
                          <span className="stat-label">Needs Assignment</span>
                          <span className="stat-value warning">{div.unassignedStates} states</span>
                        </div>
                        {div.stateCount > 0 && (
                          <div className="analytics-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ 
                                  width: `${(div.assignedStates / div.stateCount) * 100}%`,
                                  backgroundColor: div.color
                                }}
                              />
                            </div>
                            <span className="progress-label">
                              {Math.round((div.assignedStates / div.stateCount) * 100)}% assigned
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* User Analytics */}
              <div className="analytics-section">
                <h3>Rep Assignments</h3>
                {analytics.users.length === 0 ? (
                  <div className="empty-state">
                    <FiUsers size={48} />
                    <p>No reps have state assignments yet</p>
                  </div>
                ) : (
                  <div className="analytics-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Rep Name</th>
                          <th>Division</th>
                          <th>States</th>
                          <th>State List</th>
                          <th>Total Population</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.users.map((user) => (
                          <tr key={user.id}>
                            <td className="user-name">{user.name}</td>
                            <td>
                              {user.divisionName && (
                                <span 
                                  className="division-badge" 
                                  style={{ backgroundColor: user.divisionColor }}
                                >
                                  {user.divisionName}
                                </span>
                              )}
                            </td>
                            <td className="stat-number">{user.stateCount}</td>
                            <td className="state-list">{user.states || '-'}</td>
                            <td className="stat-number">
                              {user.totalPopulation ? user.totalPopulation.toLocaleString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : viewMode === "map" ? (
        <div className="map-layout">
          <div className="map-view">
          <div className="map-filter">
            <label>View by:</label>
            <select 
              value={metricFilter} 
              onChange={(e) => setMetricFilter(e.target.value)}
              className="metric-filter-select"
            >
              <option value="division">Division</option>
              <option value="people">People</option>
              <option value="companies">Companies</option>
              <option value="sales">Sales ($)</option>
              <option value="boxes">Boxes</option>
            </select>
          </div>
          
          <div className="map-legend">
            {metricFilter === 'division' ? (
              <>
                {divisions.map((div) => (
                  <React.Fragment key={div.id}>
                    <div className="legend-item">
                      <div
                        className="legend-color"
                        style={{ backgroundColor: div.color }}
                      />
                      <span>{div.name} (assigned)</span>
                    </div>
                    <div className="legend-item">
                      <div
                        className="legend-color"
                        style={{ 
                          backgroundColor: div.color,
                          opacity: 0.4
                        }}
                      />
                      <span>{div.name} (no rep)</span>
                    </div>
                  </React.Fragment>
                ))}
                <div className="legend-separator" />
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: "#E5E7EB" }} />
                  <span>No division</span>
                </div>
              </>
            ) : (
              <>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: "#3B82F6" }} />
                  <span>High</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: "#7BA8F0" }} />
                  <span>Medium</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: "#E4F0F6" }} />
                  <span>Low</span>
                </div>
                <div className="legend-separator" />
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: "#E5E7EB" }} />
                  <span>None</span>
                </div>
              </>
            )}
          </div>
          
          <div className="map-region-note">
            <strong>Note:</strong> States with regional splits (e.g., California North/South) display as one shape on the map. View grid or click state for separate region assignments.
          </div>
          
          <ComposableMap 
            projection="geoAlbersUsa"
            projectionConfig={{
              scale: 1000
            }}
            width={960}
            height={600}
          >
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const stateData = getStateByName(geo.properties.name);
                  const isHovered = hoveredState === geo.properties.name;
                  
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getStateColor(stateData)}
                      stroke="#FFFFFF"
                      strokeWidth={isHovered ? 2 : 0.5}
                      style={{
                        default: { outline: "none" },
                        hover: {
                          outline: "none",
                          cursor: "pointer",
                          opacity: 0.8
                        },
                        pressed: { outline: "none" }
                      }}
                      onMouseEnter={() => setHoveredState(geo.properties.name)}
                      onMouseLeave={() => setHoveredState(null)}
                      onClick={() => {
                        if (stateData) {
                          handleStateClick(stateData);
                        }
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          {hoveredState && getStateByName(hoveredState) && (() => {
            const stateData = getStateByName(hoveredState);
            const metrics = stateMetrics.find(m => 
              m.stateId === stateData.id && 
              ((m.regionId === stateData.regionId) || (!m.regionId && !stateData.regionId))
            );
            
            return (
              <div className="map-tooltip">
                <strong>{stateData.regionFullName || stateData.name}</strong>
                {metricFilter === 'division' ? (
                  <>
                    {stateData.divisionName && (
                      <div>Division: {stateData.divisionName}</div>
                    )}
                    {stateData.userName && (
                      <div>User: {stateData.userName}</div>
                    )}
                  </>
                ) : metrics && (
                  <div>
                    {metricFilter === 'people' && `People: ${metrics.peopleCount.toLocaleString()}`}
                    {metricFilter === 'companies' && `Companies: ${metrics.companyCount.toLocaleString()}`}
                    {metricFilter === 'sales' && `Sales: $${metrics.salesTotal.toLocaleString()}`}
                    {metricFilter === 'boxes' && `Boxes: ${metrics.boxCount.toLocaleString()}`}
                  </div>
                )}
              </div>
            );
          })()}
          </div>

          {selectedState && (
            <div className="map-counties-panel">
              <div className="counties-header">
                <div>
                  <h3>
                    {selectedState.regionFullName || selectedState.name}
                    {counties.length > 0 && ` (${counties.length} counties)`}
                  </h3>
                  {selectedState.divisionName && (
                    <div 
                      className="state-division-badge" 
                      style={{ 
                        backgroundColor: selectedState.divisionColor,
                        color: 'white'
                      }}
                    >
                      {selectedState.divisionName}
                    </div>
                  )}
                  {selectedState.userName && (
                    <div className="state-user-info">
                      <FiUsers /> {selectedState.userName}
                    </div>
                  )}
                </div>
                <button
                  className="btn-primary"
                  onClick={() => openAssignModal(selectedState)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FiEdit2 /> Edit Assignment
                </button>
              </div>
              <div className="counties-list">
                {counties.length === 0 ? (
                  <div className="empty-state">
                    <FiMapPin size={48} />
                    <p>No counties loaded yet</p>
                    <p className="empty-state-hint">
                      County data needs to be imported for this state
                    </p>
                  </div>
                ) : (
                  counties.map((county) => (
                    <div key={county.id} className="county-item">
                      <div className="county-info">
                        <div className="county-name">{county.name}</div>
                        {county.population && (
                          <div className="county-population">
                            Population: {county.population.toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="states-layout">
        <div className="states-list">
          <div className="states-list-header">
            <h3>States ({states.length})</h3>
          </div>
          <div className="states-grid">
            {states.map((state) => (
              <div
                key={`${state.id}-${state.regionId || 'full'}-${state.assignmentId || 'unassigned'}`}
                className={`state-card ${state.regionName ? 'has-region' : ''} ${selectedState?.assignmentId === state.assignmentId ? "active" : ""}`}
              >
                <div onClick={() => handleStateClick(state)} style={{ flex: 1 }}>
                  <div className="state-abbr">{state.abbreviation}{state.regionName && ` ${state.regionName.charAt(0)}`}</div>
                  <div className="state-name">{state.regionFullName || state.name}</div>
                  {state.divisionName && (
                    <div 
                      className="state-division" 
                      style={{ 
                        backgroundColor: state.divisionColor || '#6B7280',
                        color: 'white',
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        marginTop: '4px'
                      }}
                    >
                      {state.divisionName}
                    </div>
                  )}
                  {state.userName && (
                    <div className="state-user" style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      <FiUsers size={10} /> {state.userName}
                    </div>
                  )}
                </div>
                <button
                  className="btn-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAssignModal(state);
                  }}
                  title="Assign"
                  style={{ alignSelf: 'flex-start', marginTop: '4px' }}
                >
                  <FiEdit2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {selectedState && (
          <div className="counties-panel">
            <div className="counties-header">
              <h3>
                {selectedState.name} - Counties
                {counties.length > 0 && ` (${counties.length})`}
              </h3>
            </div>
            <div className="counties-list">
              {counties.length === 0 ? (
                <div className="empty-state">
                  <FiMapPin size={48} />
                  <p>No counties loaded yet</p>
                  <p className="empty-state-hint">
                    County data needs to be imported for this state
                  </p>
                </div>
              ) : (
                counties.map((county) => (
                  <div key={county.id} className="county-item">
                    <div className="county-info">
                      <div className="county-name">{county.name}</div>
                      {county.population && (
                        <div className="county-population">
                          Population: {county.population.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        </div>
      )}

      {/* Assign State Modal */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Assign {selectedState?.name}</h3>
              <button className="close-button" onClick={closeAssignModal}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleAssignSubmit}>
              {stateRegions.length > 0 && (
                <div className="form-group">
                  <label>Region</label>
                  <select
                    value={assignFormData.regionId}
                    onChange={(e) => setAssignFormData({ ...assignFormData, regionId: e.target.value })}
                    className="form-control"
                  >
                    <option value="">-- Entire State --</option>
                    {stateRegions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.fullName}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Select a specific region or leave as "Entire State"
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>Division</label>
                <select
                  value={assignFormData.divisionId}
                  onChange={(e) => setAssignFormData({ ...assignFormData, divisionId: e.target.value })}
                  className="form-control"
                >
                  <option value="">-- No Division --</option>
                  {divisions.map((div) => (
                    <option key={div.id} value={div.id}>
                      {div.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Assigned User</label>
                <select
                  value={assignFormData.userId}
                  onChange={(e) => setAssignFormData({ ...assignFormData, userId: e.target.value })}
                  className="form-control"
                >
                  <option value="">-- No User --</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name} ({member.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={assignFormData.notes}
                  onChange={(e) => setAssignFormData({ ...assignFormData, notes: e.target.value })}
                  className="form-control"
                  rows="3"
                  placeholder="Optional notes about this assignment"
                />
              </div>
              <div className="modal-actions">
                {selectedState?.assignmentId && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => handleRemoveAssignment(selectedState.assignmentId)}
                  >
                    Remove Assignment
                  </button>
                )}
                <button type="button" className="btn-secondary" onClick={closeAssignModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Assignment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default States;

